import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { partnerApiService } from '../../services/api';
import { QrLink, QrSource, QrTarget } from '../../types/api';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * QR-таблички и NFC-метки со столиков.
 *
 * Партнёр собирает ссылку здесь и печатает её сам. Адрес приходит с бэка
 * целиком — кабинет только рисует по нему картинку кода. Собирать ссылку
 * строками на фронте нельзя: формат обязан жить в одном месте, потому что
 * напечатанную табличку уже не исправить.
 */

/**
 * Уровень коррекции ошибок.
 *
 * `Q` (25%) вместо стандартного `M` (15%) намеренно: код живёт на столе в
 * зале, где на него проливают, ставят тарелки и трут салфеткой. Плотность
 * кода растёт, но ссылка короткая — модули остаются крупными.
 */
const ERROR_CORRECTION = 'Q' as const;

/** Сторона картинки в пикселях: хватает и на экран, и на печать 5×5 см. */
const QR_SIZE = 640;

/** Поля вокруг кода в модулях. Меньше четырёх — камеры начинают промахиваться. */
const QR_MARGIN = 4;
/** Максимум столиков за раз — тот же потолок, что и на бэке. */
const MAX_BATCH = 200;

type Mode = 'single' | 'batch';

/** Картинки кодов: ссылка → data-URL. Рисуются один раз на ссылку. */
type QrImages = Record<string, string>;

function fileNameFor(link: QrLink, extension: string): string {
  const place = link.table === null ? link.target : `table-${link.table}`;
  return `qprice-${link.source}-${place}.${extension}`;
}

/** Отдаёт пользователю файл, не оставляя за собой blob-URL. */
function download(href: string, fileName: string): void {
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export function QrCodesPage(): React.ReactElement {
  const { t } = useLanguage();
  const [target, setTarget] = useState<QrTarget>('menu');
  const [source, setSource] = useState<QrSource>('qr');
  const [mode, setMode] = useState<Mode>('single');
  const [from, setFrom] = useState('1');
  const [to, setTo] = useState('10');

  const targetOptions = useMemo<ReadonlyArray<{ value: QrTarget; label: string; hint: string }>>(
    () => [
      { value: 'menu', label: t.cabinet.qrTargetsMenu, hint: t.cabinet.qrTargetsMenuHint },
      { value: 'partner', label: t.cabinet.qrTargetsPartner, hint: t.cabinet.qrTargetsPartnerHint },
    ],
    [t],
  );

  const sourceOptions = useMemo<ReadonlyArray<{ value: QrSource; label: string; hint: string }>>(
    () => [
      { value: 'qr', label: t.cabinet.qrSourceQr, hint: t.cabinet.qrSourceQrHint },
      { value: 'nfc', label: t.cabinet.qrSourceNfc, hint: t.cabinet.qrSourceNfcHint },
    ],
    [t],
  );

  const [links, setLinks] = useState<QrLink[]>([]);
  const [images, setImages] = useState<QrImages>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [copiedImageUrl, setCopiedImageUrl] = useState<string | null>(null);

  /**
   * Номер последнего отправленного запроса.
   *
   * Запросы уходят при каждом переключении цели, носителя и режима, а
   * приходят не в том же порядке. Без этой отсечки медленный ответ на
   * отменённый выбор перезатирает свежий результат, и партнёр печатает
   * не тот код, который видит на экране.
   */
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const current = requestId.current + 1;
    requestId.current = current;

    setIsLoading(true);
    setError(null);

    try {
      const result =
        mode === 'single'
          ? [await partnerApiService.getQrLink({ target, source })]
          : await partnerApiService.getQrLinksForTables(Number(from), Number(to), {
              target,
              source,
            });

      if (requestId.current !== current) return;
      setLinks(result);
    } catch (e) {
      if (requestId.current !== current) return;
      setLinks([]);
      setError('Не удалось получить ссылки. Проверьте соединение и попробуйте ещё раз.');
    } finally {
      if (requestId.current === current) setIsLoading(false);
    }
  }, [mode, target, source, from, to]);

  // Одиночная ссылка перестраивается сама при смене цели или носителя:
  // это один запрос, и лишняя кнопка «показать» тут только мешает.
  //
  // Переключение в режим столиков, наоборот, сразу гасит показанное: коды
  // прошлого режима под заголовком «Коды: N шт.» читались бы как результат
  // нового запроса, а напечатать не тот код — это выброшенная бумага.
  useEffect(() => {
    if (mode === 'single') {
      void load();
      return;
    }

    requestId.current += 1;
    setLinks([]);
    setIsLoading(false);
  }, [mode, target, source, load]);

  // Картинки рисуются из ссылок, а не в момент запроса: набор ссылок может
  // прийти пачкой в двести штук, и рисовать их синхронно в обработчике
  // значило бы подвесить вкладку.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const drawn: QrImages = {};

      for (const link of links) {
        drawn[link.url] = await QRCode.toDataURL(link.url, {
          errorCorrectionLevel: ERROR_CORRECTION,
          width: QR_SIZE,
          margin: QR_MARGIN,
          color: { dark: '#16201aff', light: '#ffffffff' },
        });
      }

      if (!cancelled) setImages(drawn);
    })();

    return () => {
      cancelled = true;
    };
  }, [links]);

  const batchSize = useMemo(() => {
    const start = Number(from);
    const end = Number(to);
    if (!Number.isInteger(start) || !Number.isInteger(end)) return null;
    return end - start + 1;
  }, [from, to]);

  const batchError = useMemo(() => {
    if (mode !== 'batch' || batchSize === null) return null;
    if (batchSize < 1) return 'Номер «до» не может быть меньше номера «с».';
    if (batchSize > MAX_BATCH) return `За раз можно получить не больше ${MAX_BATCH} табличек.`;
    return null;
  }, [mode, batchSize]);

  const copy = async (url: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      window.setTimeout(() => setCopiedUrl(null), 2000);
    } catch {
      // Буфер обмена недоступен (нет https или отказали в доступе) — ссылка
      // видна на экране и её можно выделить руками, ронять экран незачем.
      setError('Не удалось скопировать. Выделите ссылку и скопируйте вручную.');
    }
  };

  /**
   * PNG кода в буфер обмена — вставить в мессенджер или в макет таблички
   * (ADR-028). Блоб уходит в ClipboardItem обещанием, без await перед write:
   * Safari иначе не считает запись действием пользователя и отказывает.
   */
  const copyImage = async (link: QrLink): Promise<void> => {
    const dataUrl = images[link.url];
    if (!dataUrl) return;
    try {
      if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
        throw new Error('Clipboard images are not supported');
      }
      const png = fetch(dataUrl).then((response) => response.blob());
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
      setCopiedImageUrl(link.url);
      window.setTimeout(() => setCopiedImageUrl(null), 2000);
    } catch {
      // Старый браузер или отказ в доступе — PNG и SVG рядом, ронять экран незачем.
      setError(t.cabinet.qrCopyImageFailed);
    }
  };

  const downloadSvg = async (link: QrLink): Promise<void> => {
    // SVG, а не PNG: табличку печатают крупно, и вектор не рассыпается
    // на пиксели при увеличении.
    const svg = await QRCode.toString(link.url, {
      type: 'svg',
      errorCorrectionLevel: ERROR_CORRECTION,
      margin: QR_MARGIN,
    });

    const blobUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    download(blobUrl, fileNameFor(link, 'svg'));
    URL.revokeObjectURL(blobUrl);
  };

  return (
    <div className="qp-stack">
      <section className="qp-panel">
        <header className="qp-panel-head">
          <div>
            <h2 className="qp-label">{t.cabinet.qrTitle}</h2>
            <p className="qp-card-sub" style={{ marginTop: 4 }}>
              {t.cabinet.qrPageSub}
            </p>
          </div>
        </header>

        <div className="qp-notice qp-notice--accent" role="note">
          {t.cabinet.qrNotice}
        </div>

        <div className="qp-grid" style={{ gap: 20, marginTop: 20 }}>
          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend className="qp-label" style={{ marginBottom: 8 }}>{t.cabinet.qrTargetLegend}</legend>
            <div className="qp-chips">
              {targetOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`qp-chip${target === option.value ? ' qp-chip--accent' : ''}`}
                  onClick={() => setTarget(option.value)}
                  aria-pressed={target === option.value}
                  title={option.hint}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="qp-card-sub" style={{ marginTop: 6 }}>
              {targetOptions.find((o) => o.value === target)?.hint}
            </p>
          </fieldset>

          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend className="qp-label" style={{ marginBottom: 8 }}>{t.cabinet.qrSourceLegend}</legend>
            <div className="qp-chips">
              {sourceOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`qp-chip${source === option.value ? ' qp-chip--accent' : ''}`}
                  onClick={() => setSource(option.value)}
                  aria-pressed={source === option.value}
                  title={option.hint}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="qp-card-sub" style={{ marginTop: 6 }}>
              {sourceOptions.find((o) => o.value === source)?.hint}
            </p>
          </fieldset>

          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend className="qp-label" style={{ marginBottom: 8 }}>{t.cabinet.qrCountLegend}</legend>
            <div className="qp-chips">
              <button
                type="button"
                className={`qp-chip${mode === 'single' ? ' qp-chip--accent' : ''}`}
                onClick={() => setMode('single')}
                aria-pressed={mode === 'single'}
              >
                {t.cabinet.qrModeSingle}
              </button>
              <button
                type="button"
                className={`qp-chip${mode === 'batch' ? ' qp-chip--accent' : ''}`}
                onClick={() => setMode('batch')}
                aria-pressed={mode === 'batch'}
              >
                {t.cabinet.qrModeBatch}
              </button>
            </div>

            {mode === 'batch' && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
                <label className="qp-field" style={{ maxWidth: 120 }}>
                  <span className="qp-label">{t.cabinet.qrTableFrom}</span>
                  <input
                    className="qp-input"
                    type="number"
                    min={1}
                    max={9999}
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  />
                </label>
                <label className="qp-field" style={{ maxWidth: 120 }}>
                  <span className="qp-label">{t.cabinet.qrTableTo}</span>
                  <input
                    className="qp-input"
                    type="number"
                    min={1}
                    max={9999}
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="qp-btn qp-btn--primary"
                  onClick={() => void load()}
                  disabled={isLoading || batchError !== null}
                >
                  {t.cabinet.qrShowCodes}
                </button>
              </div>
            )}

            {batchError && (
              <p className="qp-card-sub" style={{ marginTop: 8, color: 'var(--qp2-danger, #b4443a)' }}>
                {batchError}
              </p>
            )}
          </fieldset>
        </div>
      </section>

      {error && <div className="qp-notice qp-notice--error">{error}</div>}

      <section className="qp-panel qp-print-area">
        <header className="qp-panel-head qp-print-hide">
          <div>
            <h2 className="qp-label">{mode === 'single' ? t.cabinet.qrYourCode : `${t.cabinet.qrCodesCount}: ${links.length}`}</h2>
            <p className="qp-card-sub" style={{ marginTop: 4 }}>
              {t.cabinet.qrPrintNotice}
            </p>
          </div>

          {links.length > 0 && (
            <button type="button" className="qp-btn qp-btn--outline qp-btn--sm" onClick={() => window.print()}>
              {t.cabinet.qrPrint}
            </button>
          )}
        </header>

        {isLoading && <div className="qp-skel" style={{ height: 220 }} />}

        {!isLoading && links.length === 0 && (
          <div className="qp-empty">
            <p>{t.cabinet.qrEmpty}</p>
          </div>
        )}

        {!isLoading && links.length > 0 && (
          <div className="qp-qr-grid">
            {links.map((link) => (
              <article className="qp-qr-card" key={link.url}>
                {images[link.url] ? (
                  <img className="qp-qr-img" src={images[link.url]} alt={`QR: ${link.label}`} />
                ) : (
                  <div className="qp-skel qp-qr-img" />
                )}

                <p className="qp-qr-caption">{link.label}</p>

                <div className="qp-qr-actions qp-print-hide">
                  <button type="button" className="qp-btn qp-btn--ghost qp-btn--sm" onClick={() => void copy(link.url)}>
                    {copiedUrl === link.url ? t.cabinet.qrCopied : t.cabinet.qrCopyUrl}
                  </button>
                  <button
                    type="button"
                    className="qp-btn qp-btn--ghost qp-btn--sm"
                    disabled={!images[link.url]}
                    onClick={() => void copyImage(link)}
                  >
                    {copiedImageUrl === link.url ? t.cabinet.qrCopied : t.cabinet.qrCopyImage}
                  </button>
                  <button
                    type="button"
                    className="qp-btn qp-btn--ghost qp-btn--sm"
                    onClick={() => images[link.url] && download(images[link.url], fileNameFor(link, 'png'))}
                  >
                    PNG
                  </button>
                  <button type="button" className="qp-btn qp-btn--ghost qp-btn--sm" onClick={() => void downloadSvg(link)}>
                    SVG
                  </button>
                </div>

                <p className="qp-mono qp-qr-url qp-print-hide">{link.url}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="qp-panel qp-print-hide">
        <h2 className="qp-label">{t.cabinet.qrHowItWorks}</h2>
        <ul className="qp-hairlist" style={{ marginTop: 10 }}>
          <li>{t.cabinet.qrHowStep1}</li>
          <li>{t.cabinet.qrHowStep2}</li>
          <li>{t.cabinet.qrHowStep3}</li>
          <li>{t.cabinet.qrHowStep4}</li>
        </ul>
      </section>
    </div>
  );
}
